import React, { useEffect, useState } from "react";
import { Modal, Table, InputNumber, message, Select } from "antd";
import axios from "./Services/Api";

const { Option } = Select;

/* ───────────────────────── helpers */
const allowOnlyNumbers = (e) => {
  if (!/[0-9.]/.test(e.key)) e.preventDefault();
};

/* Get valid units for an item */
const getUnitsForItem = (item) => {
  // 1️⃣ Custom unit items
  if (item.customUnits?.length) {
    return item.customUnits.map((u) => `${u.qty}${u.unit}`);
  }

  // 2️⃣ Weight based items
  const units = [];
  if (item.price1kg > 0) units.push("1kg");
  if (item.price500gm > 0) units.push("500gm");
  if (item.price250gm > 0) units.push("250gm");

  // 3️⃣ 🔥 DEFAULT FALLBACK (THIS FIXES CHICKEN WRAP)
  if (units.length === 0) {
    units.push("default"); // or "plate" / "pcs"
  }

  return units;
};


const ProfilePricing = ({ open, onClose, restaurantId, menuItems = [] }) => {
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(false);
  const [rowUnit, setRowUnit] = useState({}); // { itemId: unit }

  /* ───────────────────────── load existing prices */
  useEffect(() => {
    const loadPrices = async () => {
      if (!open || !restaurantId) return;

      setLoading(true);
      try {
        const { data } = await axios.get("/getProfilePrices", {
          params: { restaurantId },
        });

        const fromDb = {};
        data.forEach((item) => {
          fromDb[item.itemId] = {};
          item.units.forEach((u) => {
            fromDb[item.itemId][u.unit] = {
              p1: u.profile1 ?? "",
              p2: u.profile2 ?? "",
              p3: u.profile3 ?? "",
            };
          });
        });

        // Merge with menu items & valid units
        const merged = {};
        const unitDefaults = {};

        menuItems.forEach((item) => {
          const units = getUnitsForItem(item);
          merged[item._id] = {};

          units.forEach((u) => {
            merged[item._id][u] =
              fromDb[item._id]?.[u] || { p1: "", p2: "", p3: "" };
          });

          // default selected unit per row
          unitDefaults[item._id] = units[0];
        });

        setPrices(merged);
        setRowUnit(unitDefaults);
      } catch (err) {
        console.error(err);
        message.error("Failed to load profile prices.");
      } finally {
        setLoading(false);
      }
    };

    loadPrices();
  }, [open, restaurantId, menuItems]);

  /* ───────────────────────── update price */
  const update = (itemId, unit, key, value) => {
    setPrices((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [unit]: {
          ...prev[itemId]?.[unit],
          [key]: value,
        },
      },
    }));
  };

  /* ───────────────────────── save */
  const save = async () => {
    try {
      const payload = [];

      Object.entries(prices).forEach(([itemId, units]) => {
        Object.entries(units).forEach(([unit, p]) => {
          payload.push({
            itemId,
            unit,
            profile1: p.p1,
            profile2: p.p2,
            profile3: p.p3,
          });
        });
      });

      await axios.post("/saveProfilePrices", {
        restaurantId,
        prices: payload,
      });

      message.success("Profile prices saved successfully");
      onClose();
    } catch (err) {
      console.error(err);
      message.error("Failed to save profile prices.");
    }
  };

  /* ───────────────────────── render */
  return (
    <Modal
      title="Profile Pricing"
      open={open}
      onCancel={onClose}
      onOk={save}
      okText="Save"
      width={1000}
      destroyOnClose
      confirmLoading={loading}
    >
      <Table
  rowKey="_id"
  dataSource={menuItems}
  pagination={false}
  size="small"
  columns={[
    {
      title: "Item",
      dataIndex: "name",
      width: 220,
    },
    {
      title: "Unit",
      width: 140,
      render: (_, rec) => {
        const units = getUnitsForItem(rec);
        return (
          <Select
            value={rowUnit[rec._id]}
            style={{ width: "100%" }}
            onChange={(u) =>
              setRowUnit((p) => ({ ...p, [rec._id]: u }))
            }
          >
            {units.map((u) => (
              <Option key={u} value={u}>
                {u}
              </Option>
            ))}
          </Select>
        );
      },
    },
    ...["p1", "p2", "p3"].map((key, idx) => ({
      title: `Profile ${idx + 1}`,
      render: (_, rec) => {
        const unit = rowUnit[rec._id];
        return (
          <InputNumber
            min={0}
            precision={2}
            step={0.01}
            style={{ width: "100%" }}
            value={prices[rec._id]?.[unit]?.[key] ?? ""}
            onChange={(v) => update(rec._id, unit, key, v)}
          />
        );
      },
    })),
  ]}
/>

    </Modal>
  );
};

export default ProfilePricing;
